import { css, StyleSheet } from 'aphrodite';
import { FC, useCallback, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { BlogFont } from '../../../extViewmodels';
import { parseGoogleFontInput } from '../../../utils/googleFontUtils';
import { Collapsible } from '../../../web/components/Collapsible';
import { DeprecatedSelect, ISelectOption } from '../../../web/components/DeprecatedSelect';
import { background, black, charmGray, shuffleGray } from '../../../web/styles/colors';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import { LabeledInput } from '../LabeledInput/LabeledInput';
import { Column, Row } from '../Layout';

interface FontSource {
	source: 'google-font' | 'web-font';
}
interface FontSelectProps {
	onChange: (selectedFont: BlogFont) => void;
	errors?: { fontName?: FieldError; fontUri?: FieldError };
	initialValue: BlogFont;
	value: BlogFont;
}

const FONT_SOURCE_OPTIONS = [
	{
		dataContext: {
			source: 'google-font' as const,
		},
		id: 'google-font-option',
		text: 'Google Font',
	},
	{
		dataContext: {
			source: 'web-font' as const,
		},
		id: 'web-font-option',
		text: 'Web Default Font',
	},
];
const WEB_FONT_OPTIONS = [
	{
		dataContext: {
			fontName: 'Arial',
		},
		id: 'arial-font-option',
		text: 'Arial',
	},
	{
		dataContext: {
			fontName: 'Verdana',
		},
		id: 'verdana-font-option',
		text: 'Verdana',
	},
	{
		dataContext: {
			fontName: 'Tahoma',
		},
		id: 'tahoma-font-option',
		text: 'Tahoma',
	},
	{
		dataContext: {
			fontName: 'Trebuchet MS',
		},
		id: 'trebuchet-ms-font-option',
		text: 'Trebuchet MS',
	},
	{
		dataContext: {
			fontName: 'Times New Roman',
		},
		id: 'tnr-font-option',
		text: 'Times New Roman',
	},
	{
		dataContext: {
			fontName: 'Georgia',
		},
		id: 'georgia-font-option',
		text: 'Georgia',
	},
	{
		dataContext: {
			fontName: 'Garamond',
		},
		id: 'garamond-font-option',
		text: 'Garamond',
	},
	{
		dataContext: {
			fontName: 'Courier New',
		},
		id: 'courier-new-font-option',
		text: 'Courier New',
	},
	{
		dataContext: {
			fontName: 'Brush Script MT',
		},
		id: 'brush-script-font-option',
		text: 'Brush Script MT',
	},
];

export const FontSelect: FC<FontSelectProps> = ({ errors, initialValue, onChange, value }) => {
	const [fontSource, setFontSource] = useState<ISelectOption<FontSource>>();
	const [selectedOption, setSelectedOption] = useState<ISelectOption<BlogFont>>();

	const handleWebFontSelect = useCallback(
		(option: ISelectOption<BlogFont>) => {
			setSelectedOption(option);
			onChange({ fontUri: '', ...option.dataContext });
		},
		[onChange]
	);
	const handleFontUriChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const inputValue = e.target.value;
			const { fontUri, fontName } = parseGoogleFontInput(inputValue);
			onChange({ fontName, fontUri });
		},
		[onChange]
	);

	useEffect(() => {
		if (initialValue?.fontUri && fontSource?.dataContext?.source !== 'google-font') {
			setFontSource(FONT_SOURCE_OPTIONS[0]);
		} else if (initialValue?.fontName && !initialValue?.fontUri && fontSource?.dataContext?.source !== 'web-font') {
			const option = WEB_FONT_OPTIONS.find(val => val.dataContext.fontName === initialValue.fontName);

			setFontSource(FONT_SOURCE_OPTIONS[1]);
			setSelectedOption(option);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue]);

	return (
		<>
			<Column>
				<label className={css(fontSelectStyleSheet.label, fontSelectStyleSheet.labelRow)}>Font Source</label>
				<DeprecatedSelect
					onOptionClick={val => setFontSource(val)}
					options={FONT_SOURCE_OPTIONS}
					selectedOption={fontSource}
					styles={[fontSelectStyleSheet.container]}
				/>
			</Column>
			<Column className={css(fontSource ? null : fontSelectStyleSheet.collapsible)}>
				<Collapsible isCollapsed={!(fontSource?.dataContext.source === 'web-font')}>
					<Column>
						<Row className={css(fontSelectStyleSheet.labelRow)}>
							<label className={css(fontSelectStyleSheet.label)}>Web Default Font</label>
						</Row>
						<DeprecatedSelect
							onOptionClick={handleWebFontSelect}
							options={WEB_FONT_OPTIONS}
							selectedOption={selectedOption}
							styles={[fontSelectStyleSheet.container]}
						/>
					</Column>
				</Collapsible>
				<Collapsible isCollapsed={!(fontSource?.dataContext.source === 'google-font')}>
					<LabeledInput
						error={errors?.fontUri}
						label='Google Font URL'
						labelAccessory={
							<InfoPopover message='To use a Google Font, visit https://fonts.google.com/ and select a font. Copy the URL from the "Embed" tab and paste it here. You can paste either the URL directly or the full <link> tag.' />
						}
						onChange={handleFontUriChange}
						value={value?.fontUri ?? ''}
					/>
				</Collapsible>
			</Column>
		</>
	);
};

export const fontSelectStyleSheet = StyleSheet.create({
	collapsible: {
		display: 'none',
	},
	container: {
		backgroundColor: background,
		border: `1px solid ${shuffleGray}`,
		borderRadius: 8,
		color: black,
		height: 44,
		width: '100%',
		minWidth: '100%',
	},
	info: {
		fontSize: 12,
		color: charmGray,
		textAlign: 'right',
	},
	label: {
		fontSize: 14,
	},
	labelRow: {
		gap: 4,
		paddingBottom: 8,
	},
	spacer: {
		paddingBottom: 24,
	},
});
